import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import '../../global.css';
import NeuralCanvas from '../../components/NeuralCanvas';
import WordmarkLogo from '../../components/WordmarkLogo';
import AuthFooter from '../../components/AuthFooter';
import api from '../../store/axios';

const IconMail = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m2 7 10 7 10-7"/></svg>;
const IconCheck = () => <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="M22 4 12 14.01l-3-3"/></svg>;
const IconRefresh = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>;

const RESEND_WAIT = 60;

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, accessToken } = useSelector(s => s.auth);

  const [countdown, setCountdown] = useState(RESEND_WAIT);
  const [resendStatus, setResendStatus] = useState('idle');
  const [verifyStatus, setVerifyStatus] = useState('idle');

  const email = user?.email || params.get('email') || '';
  const token = params.get('token');

  useEffect(() => {
    if (!token) return;
    setVerifyStatus('verifying');
    api.post('/auth/verify-email', null, { params: { token } })
      .then(() => {
        setVerifyStatus('success');
        setTimeout(() => navigate('/dashboard'), 2200);
      })
      .catch(() => setVerifyStatus('error'));
  }, [token, navigate]);

  useEffect(() => {
    if (countdown <= 0) return;
    const id = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [countdown]);

  const handleResend = async () => {
    if (countdown > 0 || resendStatus === 'sending') return;
    setResendStatus('sending');
    try {
      await api.post('/auth/resend-verification', { email });
      setResendStatus('sent');
      setCountdown(RESEND_WAIT);
      setTimeout(() => setResendStatus('idle'), 4000);
    } catch {
      setResendStatus('error');
      setTimeout(() => setResendStatus('idle'), 3000);
    }
  };

  if (verifyStatus === 'verifying') {
    return (
      <div className="auth-layout">
        <NeuralCanvas />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
          <span className="btn-loader" style={{ width: 24, height: 24, borderWidth: 2 }} />
          <p style={{ fontSize: 14, color: 'var(--fg-mid)' }}>Verifying your email…</p>
        </div>
        <AuthFooter />
      </div>
    );
  }

  if (verifyStatus === 'success') {
    return (
      <div className="auth-layout">
        <NeuralCanvas />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(107,255,184,0.12)', border: '1px solid rgba(107,255,184,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: 'var(--signal)' }}><IconCheck /></span>
          </div>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontSize: 22, fontWeight: 500, letterSpacing: '-0.02em', marginBottom: 8 }}>Email verified.</h2>
            <p style={{ fontSize: 14, color: 'var(--fg-mid)' }}>Taking you to the dashboard…</p>
          </div>
        </div>
        <AuthFooter />
      </div>
    );
  }

  return (
    <div className="auth-layout">
      <NeuralCanvas />

      <div className="signup-shell">
        <div className="signup-left">
          <WordmarkLogo size={22} />
          <div style={{ margin: 'auto 0', display: 'flex', flexDirection: 'column', gap: 32, maxWidth: 440 }}>
            <div className="dash-sub">ACCOUNT ACTIVATION</div>
            <h1 className="signup-title">One more<br /><em>step.</em></h1>
            <p className="signup-sub">
              We sent a verification link to your inbox. It expires in 60 minutes.
              Check your spam folder if you can't find it.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 28, borderTop: '1px solid var(--line)' }}>
              {[
                ['Check inbox', 'Look for an email from noreply@cybernate.ai'],
                ['Click the link', 'One tap to verify — no code to copy'],
                ['Get building', 'Your API keys will be waiting'],
              ].map(([t, d], i) => (
                <div key={i} style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', letterSpacing: '0.08em', paddingTop: 1, minWidth: 24 }}>0{i + 1}</div>
                  <div>
                    <div style={{ fontSize: 14, color: 'var(--fg)', marginBottom: 3 }}>{t}</div>
                    <div style={{ fontSize: 13, color: 'var(--fg-mid)', lineHeight: 1.5 }}>{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="signup-right">
          <div className="signup-form" style={{ margin: 'auto 0' }}>
            <div style={{ width: 56, height: 56, borderRadius: 12, background: 'rgba(139,124,255,0.1)', border: '1px solid rgba(139,124,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ color: 'var(--accent)' }}><IconMail /></span>
            </div>

            <div>
              <div className="dash-sub" style={{ marginBottom: 10 }}>CHECK YOUR EMAIL</div>
              <h2 className="signup-title" style={{ fontSize: 30 }}>Verify your<br /><em>account.</em></h2>
            </div>

            <p style={{ fontSize: 14, color: 'var(--fg-mid)', lineHeight: 1.6 }}>
              We sent a confirmation link to{' '}
              <strong style={{ color: 'var(--fg)', fontWeight: 500 }}>{email || 'your email'}</strong>.
              Click it to activate your account and get access to the dashboard.
            </p>

            {verifyStatus === 'error' && (
              <div className="alert alert-error">The verification link is invalid or has expired. Request a new one below.</div>
            )}

            {resendStatus === 'sent' && (
              <div className="alert alert-success">New verification email sent — check your inbox.</div>
            )}

            {resendStatus === 'error' && (
              <div className="alert alert-error">Couldn't send the email. Please try again.</div>
            )}

            <button
              className="btn btn-primary btn-lg"
              style={{ justifyContent: 'center' }}
              onClick={handleResend}
              disabled={countdown > 0 || resendStatus === 'sending'}
            >
              {resendStatus === 'sending' ? (
                <><span className="btn-loader" /> Sending…</>
              ) : countdown > 0 ? (
                <><IconRefresh /> Resend in {countdown}s</>
              ) : (
                <><IconRefresh /> Resend verification email</>
              )}
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
              <Link to="/login" style={{ color: 'var(--fg-mid)' }}>← Back to sign in</Link>
              {accessToken && (
                <Link to="/dashboard" style={{ color: 'var(--accent)' }}>Skip for now →</Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <AuthFooter />
    </div>
  );
}
